import { useWindowSize } from 'react-use';
import { useContextualRouting } from 'next-use-contextual-routing';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { Alert, AlertTitle } from '@mui/material';
import { findIndex } from 'lodash';
import { getWindow, trackEvent } from '@surfline/web-common';
import { surfZoneCardTransformer } from '../../utils/transformers';
import ContentCarousel from '../ContentCarousel';
import ContentCarouselCard from '../ContentCarouselCard';
import type { SurfSpot } from '../../types/contentful/content-types';
import Spot from '../../containers/Spot';
import ModalContextual from '../ModalContextual';
import { doExternalFetch } from '../../common/baseFetch';
import config from '../../config';

const getNextSpot = (spotId: string | undefined, spots: SurfSpot[] | undefined) => {
  const currentSpotIndex = findIndex(spots, { spotId });

  if (spots && currentSpotIndex === spots.length - 1) {
    return spots[0];
  }
  if (spots && spots.length > 1) {
    return spots[currentSpotIndex + 1];
  }
  return null;
};

const getPreviousSpot = (spotId: string | undefined, spots: SurfSpot[] | undefined) => {
  const currentSpotIndex = findIndex(spots, { spotId });

  if (spots && currentSpotIndex === 0) {
    return spots[spots.length - 1];
  }
  if (spots && currentSpotIndex > 0) {
    return spots[currentSpotIndex - 1];
  }
  return null;
};

const getSpotLinkAs = (
  zoneSlug: string,
  spotId: string | undefined,
  spotSlug: string | undefined,
) => `/travel/zones/${zoneSlug}/surf-guide/${spotSlug?.toLocaleLowerCase()}/${spotId}`;

interface SurfZonePlaylistProps {
  data:
    | {
        items?: Array<SurfSpot> | undefined;
      }
    | undefined;
  pageTitle?: string;
  spot?: string;
  zoneSlug: string;
}

const SurfZonePlaylist: React.FC<SurfZonePlaylistProps> = ({
  data: playlistData,
  pageTitle,
  spot,
  zoneSlug,
}) => {
  const DEFAULT_COLORS = {
    bg: 'white',
    skeletonBg: '#eaeaea',
    skeletonItem: '#f7f7f7',
    headerText: '#171717',
    headerButton: '#171717',
  };

  const { makeContextualHref, returnHref } = useContextualRouting();

  const { asPath } = useRouter();

  const { width: windowWidth } = useWindowSize();

  let itemAspectRatio;

  if (windowWidth < 400) {
    itemAspectRatio = 1;
  } else if (windowWidth < 500) {
    itemAspectRatio = 1;
  } else if (windowWidth < 600) {
    itemAspectRatio = 1;
  } else if (windowWidth < 725) {
    itemAspectRatio = 1;
  } else if (windowWidth < 768) {
    itemAspectRatio = 1;
  } else if (windowWidth < 860) {
    itemAspectRatio = 1;
  } else if (windowWidth < 950) {
    itemAspectRatio = 1;
  } else if (windowWidth < 1000) {
    itemAspectRatio = 1;
  } else if (windowWidth < 1100) {
    itemAspectRatio = 1.02;
  } else if (windowWidth < 1200) {
    itemAspectRatio = 1;
  } else if (windowWidth < 1260) {
    itemAspectRatio = 1.0;
  } else if (windowWidth < 1300) {
    itemAspectRatio = 1.082;
  } else if (windowWidth < 1400) {
    itemAspectRatio = 1.08;
  } else if (windowWidth < 1500) {
    itemAspectRatio = 1.075;
  } else if (windowWidth < 1600) {
    itemAspectRatio = 1.03;
  } else {
    itemAspectRatio = 1.02;
  }

  const surfZonePlaylistCard = (itemData: SurfSpot | undefined, index: number): React.ReactNode => {
    if (!itemData) return null;

    return (
      <ContentCarouselCard
        key={`${index + 1}-card`}
        data={surfZoneCardTransformer({
          ...(itemData as Required<SurfSpot>),
          ...(itemData?.spotId && { link: makeContextualHref({ spot: itemData.spotId }) }),
          linkAs: getSpotLinkAs(zoneSlug, itemData?.spotId, itemData?.slug ?? itemData?.title),
        })}
        trackCardClick={() => {
          trackEvent('Carousel Clicked', {
            carouselItem: itemData?.title,
            carouselName: 'The Waves Modal',
            category: 'travel',
            path: asPath,
            positionNumber: index,
            referrer: `${getWindow()?.location?.href ?? ''}${asPath}`,
            search: getWindow().location.search,
            title: pageTitle,
            url: `${getWindow()?.location?.href ?? ''}${asPath}`,
          });
        }}
      />
    );
  };

  const { data: fetchData, error } = useSWR(
    () => (spot ? `${config.servicesURL}/travel/zones/spot?id=${spot}` : null),
    doExternalFetch,
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    },
  );

  const nextSpot = getNextSpot(spot, playlistData?.items);
  const previousSpot = getPreviousSpot(spot, playlistData?.items);

  return (
    <section data-testid="surf-zone" id="the-waves">
      <ModalContextual open={!!spot} returnHref={returnHref}>
        {!error ? (
          <Spot
            page={{
              spot: (fetchData as any)?.spot || {},
              links: {
                nextLink: makeContextualHref({ spot: nextSpot?.spotId || '' }),
                nextLinkAs: getSpotLinkAs(
                  zoneSlug,
                  nextSpot?.spotId,
                  nextSpot?.slug ?? nextSpot?.title,
                ),
                previousLink: makeContextualHref({ spot: previousSpot?.spotId || '' }),
                previousLinkAs: getSpotLinkAs(
                  zoneSlug,
                  previousSpot?.spotId,
                  previousSpot?.slug ?? previousSpot?.title,
                ),
              },
            }}
          />
        ) : (
          <Alert severity="error" sx={{ padding: '40px', borderRadius: '20px' }}>
            <AlertTitle>Error finding spot</AlertTitle>
            There was an issue retrieving data about this wave. Please try again.
          </Alert>
        )}
      </ModalContextual>
      <ContentCarousel
        carouselItem={surfZonePlaylistCard}
        colors={DEFAULT_COLORS}
        dataTest="surf-zone-carousel"
        itemAspectRatio={itemAspectRatio}
        numberOfitems={3}
        rawData={playlistData?.items}
        screenConfig={{ small: 2, mobile: 1, medium: 3 }}
        showPageCount
        style={{ marginTop: 0 }}
        subtitle=""
        title="The Waves"
        useSLSectionPadding
      />
    </section>
  );
};

export default SurfZonePlaylist;
